import React, { Dispatch, SetStateAction, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { SettingsDA } from '../../services/admin/settings.service';
import { GlobalSwal } from '../../styles/swal.style';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { legendIconValidInvalidRestart, legendIconInvalid, legendValid } from '../../utils/legend.data.util';
import { modalHide, modalShow } from '../../utils/modal.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { inventoryTypeValue } from '../../libraries/inventory.type.library';
import { prefixLegend, suffixLegend } from '../../scripts/name.legend.script';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';
import AppLoading from '../Loading';
import { Unit } from '../../models/unit.model';

export interface AppModalSettingCreateUnitProps {
  companyForUser: Company | null,
  options: {type: string , main_modal: string | null, index: number | string | null, sub_index: number | string | null},
  variant?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  requirements?: {list: boolean, value: any[], valid: any[]},
  setUnitMeasure?: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setVariant?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>,
  setRequirements?: Dispatch<SetStateAction<{list: boolean, value: any[], valid: any[]}>>,
  loadSettingsForCompany: Function
};

let errorResponse: Error;

const AppModalSettingCreateUnit: React.FunctionComponent<AppModalSettingCreateUnitProps> = ({companyForUser, options, variant, requirements, setUnitMeasure, setVariant, setRequirements, loadSettingsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [symbol, setSymbol] = useState({value: '', valid: false})

  const submitSettingsParameterCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && name.valid && symbol.valid) {
      await SettingsDA.postSettingMeasureUnit(companyForUser.id, options.type, name.value, symbol.value).then( (response) => {
        if (response.status === 201 || response.status === 200) {
          let createdUnit = response.data as Unit
          loadSettingsForCompany(companyForUser.id)

          if (setUnitMeasure) {
            setUnitMeasure({value: createdUnit.id, valid: true, object: createdUnit})
            legendValid(`${prefixLegend(options.main_modal)}legend-unit-required${suffixLegend(options.index, options.sub_index)}`)
          }

          if (variant && setVariant) {
            setVariant({value: {...variant.value, unit: createdUnit.id}, valid: {...variant.valid, unit: true}})
            legendValid(`${prefixLegend(options.main_modal)}legend-variant-unit-required${suffixLegend(options.index, options.sub_index)}`)
          }

          if (requirements && setRequirements) {
            let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
              if (temp_index === options.index) {
                temp_item.contents[options.sub_index!].unit = {id: createdUnit.id, name: createdUnit.name, abbreviation: createdUnit.symbol}
              }
              return temp_item
            })
            let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
              if (temp_index === options.index) {
                temp_item.contents[options.sub_index!].unit = true
              }
              return temp_item
            })

            setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
            legendValid(`${prefixLegend(options.main_modal)}legend-requirement-content-unit-required${suffixLegend(options.index, options.sub_index)}`)
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedCompanyParameters,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalSettingUnitCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value.length) legendIconInvalid(`modal-settings-create-measure-unit-input-name${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-name-required${suffixLegend(options.index, options.sub_index)}`)
        if (!symbol.value.length) legendIconInvalid(`modal-settings-create-measure-unit-input-symbol${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-symbol-required${suffixLegend(options.index, options.sub_index)}`)
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeSymbol = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setSymbol({...symbol, value: event.target.value})
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.text, name, setName, `modal-settings-create-measure-unit-input-name${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-name-valid${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-name-required${suffixLegend(options.index, options.sub_index)}`)
  }

  const validateSymbol = () => {
    evaluateLegendRequiredIcon(expressions.text, symbol, setSymbol, `modal-settings-create-measure-unit-input-symbol${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-symbol-valid${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-symbol-required${suffixLegend(options.index, options.sub_index)}`)
  }

  const executeHideModalSettingUnitCreate = () => {
    modalHide(`modal-settings-create-measure-unit${suffixLegend(options.index, options.sub_index)}`)

    if (options.main_modal) {
      modalShow(options.main_modal)
    }

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart(`modal-settings-create-measure-unit-input-name${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-name-valid${suffixLegend(options.index, options.sub_index)}`)
    legendIconValidInvalidRestart(`modal-settings-create-measure-unit-input-name${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-name-required${suffixLegend(options.index, options.sub_index)}`)
    legendIconValidInvalidRestart(`modal-settings-create-measure-unit-input-symbol${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-symbol-valid${suffixLegend(options.index, options.sub_index)}`)
    legendIconValidInvalidRestart(`modal-settings-create-measure-unit-input-symbol${suffixLegend(options.index, options.sub_index)}`, `modal-settings-create-measure-unit-legend-symbol-required${suffixLegend(options.index, options.sub_index)}`)

    setName({value: '', valid: false})
    setSymbol({value: '', valid: false})
  }

  return (
    <div id={`modal-settings-create-measure-unit${suffixLegend(options.index, options.sub_index)}`} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.addUnitMeasureParameter}</h2>
          </div>
          <div className="modal-body">
            { options.type.length > 0
              ?
              <div className="form">
                <div className="row mb-2">
                  <label className="col-lg-3 col-form-label required">{lang.labels.type}</label>
                  <div className="col-lg-9">
                    <input className="form-control is-valid" type="text" autoComplete="off" name="type" value={inventoryTypeValue(lang, options.type)} disabled />
                  </div>
                </div>
                <div className="row mb-2">
                  <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                  <div className="col-lg-9">
                    <input id={`modal-settings-create-measure-unit-input-name${suffixLegend(options.index, options.sub_index)}`} className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                    <AppLegend component={"modal-settings-create-measure-unit"} attribute={{check: name.valid, name: "name", index: options.index, sub_index: options.sub_index}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
                <div className="row">
                  <label className="col-lg-3 col-form-label required">{lang.labels.symbol}</label>
                  <div className="col-lg-9">
                    <input id={`modal-settings-create-measure-unit-input-symbol${suffixLegend(options.index, options.sub_index)}`} className="form-control" type="text" autoComplete="off" name="symbol" value={symbol.value} onChange={handleChangeSymbol} onKeyUp={validateSymbol} onBlur={validateSymbol} />
                    <AppLegend component={"modal-settings-create-measure-unit"} attribute={{check: symbol.valid, name: "symbol", index: options.index, sub_index: options.sub_index}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalSettingUnitCreate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitSettingsParameterCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalSettingCreateUnit;
